import { useState, useCallback } from 'react';
import axios from 'axios';

const useIntegrationData = ({ getAuthHeaders, apiBackend, setError, showSuccessMessage }) => {
  const [integrationStatuses, setIntegrationStatuses] = useState({});
  const [platforms, setPlatforms] = useState([]);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [isLoadingPlatforms, setIsLoadingPlatforms] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [lastImportResult, setLastImportResult] = useState(null);
  const [lastPullResult, setLastPullResult] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [isLoadingConflicts, setIsLoadingConflicts] = useState(false);
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);

  const integrationsUrl = `${apiBackend}/api/integrations`;

  const fetchPlatforms = useCallback(async () => {
    if (platforms.length > 0) return platforms;
    setIsLoadingPlatforms(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.get(`${integrationsUrl}/platforms`, config);
      if (response.data.success) {
        setPlatforms(response.data.data.platforms || []);
        return response.data.data.platforms || [];
      }
      return [];
    } catch (err) {
      console.error('Error fetching platforms:', err);
      setError?.(err.response?.data?.error || 'Failed to load integration platforms');
      return [];
    } finally {
      setIsLoadingPlatforms(false);
    }
  }, [integrationsUrl, getAuthHeaders, platforms, setError]);

  const fetchIntegrationStatuses = useCallback(async (channelIds) => {
    if (!channelIds || channelIds.length === 0) return;
    setIsLoadingStatuses(true);
    try {
      const config = await getAuthHeaders();
      const results = await Promise.allSettled(
        channelIds.map(id =>
          axios.get(`${integrationsUrl}/${id}/status`, config).then(res => ({
            channelId: id,
            ...res.data.data,
          }))
        )
      );
      const statuses = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { channelId, ...status } = result.value;
          statuses[channelId] = status;
        }
      });
      setIntegrationStatuses(statuses);
    } catch (err) {
      console.error('Error fetching integration statuses:', err);
    } finally {
      setIsLoadingStatuses(false);
    }
  }, [integrationsUrl, getAuthHeaders]);

  const connectChannel = useCallback(async (channelId, platform) => {
    setIsConnecting(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${integrationsUrl}/${channelId}/connect`,
        { platform },
        config
      );
      if (response.data.success && response.data.data.authorizationUrl) {
        window.location.href = response.data.data.authorizationUrl;
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error connecting channel:', err);
      const message = err.response?.data?.error || 'Failed to connect channel';
      setError?.(message);
      setIsConnecting(false);
      return false;
    }
  }, [integrationsUrl, getAuthHeaders, setError]);

  const disconnectChannel = useCallback(async (channelId) => {
    setIsDisconnecting(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${integrationsUrl}/${channelId}/disconnect`,
        {},
        config
      );
      if (response.data.success) {
        setIntegrationStatuses(prev => ({
          ...prev,
          [channelId]: { ...prev[channelId], connected: false, status: 'disconnected', platform: null },
        }));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error disconnecting channel:', err);
      setError?.(err.response?.data?.error || 'Failed to disconnect channel');
      // Refetch status to sync UI state on 400 errors
      if (err.response?.status === 400) {
        const config = await getAuthHeaders();
        try {
          const res = await axios.get(`${integrationsUrl}/${channelId}/status`, config);
          if (res.data.success) {
            setIntegrationStatuses(prev => ({ ...prev, [channelId]: res.data.data }));
          }
        } catch { /* ignore refetch error */ }
      }
      return false;
    } finally {
      setIsDisconnecting(false);
    }
  }, [integrationsUrl, getAuthHeaders, setError]);

  const syncInventory = useCallback(async (channelId, options = {}) => {
    setIsSyncing(true);
    setLastSyncResult(null);
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${integrationsUrl}/${channelId}/sync/inventory`,
        { direction: options.direction || 'push', itemIds: options.itemIds },
        config
      );
      if (response.data.success) {
        const result = response.data.data;
        if (result.failed > 0) {
          setLastSyncResult(result);
        } else {
          const count = result.synced || 0;
          showSuccessMessage?.(`Synced ${count} item${count !== 1 ? 's' : ''} successfully`);
        }
        // Refresh status after sync
        try {
          const statusRes = await axios.get(`${integrationsUrl}/${channelId}/status`, config);
          if (statusRes.data.success) {
            setIntegrationStatuses(prev => ({ ...prev, [channelId]: statusRes.data.data }));
          }
        } catch { /* ignore status refresh error */ }
        return result;
      }
      return null;
    } catch (err) {
      console.error('Error syncing inventory:', err);
      setError?.(err.response?.data?.error || 'Failed to sync inventory');
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [integrationsUrl, getAuthHeaders, setError, showSuccessMessage]);

  const importSales = useCallback(async (channelId, dateRange = {}) => {
    setIsImporting(true);
    setLastImportResult(null);
    try {
      const config = await getAuthHeaders();
      const body = {};
      if (dateRange.startDate) body.startDate = dateRange.startDate;
      if (dateRange.endDate) body.endDate = dateRange.endDate;
      const response = await axios.post(
        `${integrationsUrl}/${channelId}/sync/sales`,
        body,
        config
      );
      if (response.data.success) {
        const result = response.data.data;
        if (result.failed > 0) {
          setLastImportResult(result);
        } else {
          const imported = result.imported || 0;
          const skipped = result.skipped || 0;
          let msg = `Imported ${imported} sale${imported !== 1 ? 's' : ''}`;
          if (skipped > 0) msg += `, ${skipped} skipped`;
          showSuccessMessage?.(msg);
        }
        return result;
      }
      return null;
    } catch (err) {
      console.error('Error importing sales:', err);
      setError?.(err.response?.data?.error || 'Failed to import sales');
      return null;
    } finally {
      setIsImporting(false);
    }
  }, [integrationsUrl, getAuthHeaders, setError, showSuccessMessage]);

  const pullInventory = useCallback(async (channelId) => {
    setIsPulling(true);
    setLastPullResult(null);
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${integrationsUrl}/${channelId}/sync/inventory`,
        { direction: 'pull' },
        config
      );
      if (response.data.success) {
        const result = response.data.data;
        setLastPullResult(result);
        const created = result.created || 0;
        const matched = result.matched || 0;
        const conflicts = result.conflicts || 0;
        let msg = `Pull complete: ${created} new item${created !== 1 ? 's' : ''}, ${matched} matched`;
        if (conflicts > 0) msg += `, ${conflicts} conflict${conflicts !== 1 ? 's' : ''} to review`;
        showSuccessMessage?.(msg);
        // Refresh status after pull
        try {
          const statusRes = await axios.get(`${integrationsUrl}/${channelId}/status`, config);
          if (statusRes.data.success) {
            setIntegrationStatuses(prev => ({ ...prev, [channelId]: statusRes.data.data }));
          }
        } catch { /* ignore status refresh error */ }
        return result;
      }
      return null;
    } catch (err) {
      console.error('Error pulling inventory:', err);
      setError?.(err.response?.data?.error || 'Failed to pull inventory from platform');
      return null;
    } finally {
      setIsPulling(false);
    }
  }, [integrationsUrl, getAuthHeaders, setError, showSuccessMessage]);

  const fetchConflicts = useCallback(async (channelId, status = 'pending') => {
    setIsLoadingConflicts(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.get(
        `${integrationsUrl}/${channelId}/conflicts?status=${status}`,
        config
      );
      if (response.data.success) {
        setConflicts(response.data.data.conflicts || []);
        return response.data.data.conflicts || [];
      }
      return [];
    } catch (err) {
      console.error('Error fetching conflicts:', err);
      setError?.(err.response?.data?.error || 'Failed to load conflicts');
      return [];
    } finally {
      setIsLoadingConflicts(false);
    }
  }, [integrationsUrl, getAuthHeaders, setError]);

  const resolveConflict = useCallback(async (channelId, conflictId, resolution) => {
    setIsResolvingConflict(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${integrationsUrl}/${channelId}/conflicts/${conflictId}/resolve`,
        { resolution },
        config
      );
      if (response.data.success) {
        setConflicts(prev => prev.map(c =>
          c.id === conflictId ? { ...c, status: `resolved_${resolution}` } : c
        ));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error resolving conflict:', err);
      setError?.(err.response?.data?.error || 'Failed to resolve conflict');
      return false;
    } finally {
      setIsResolvingConflict(false);
    }
  }, [integrationsUrl, getAuthHeaders, setError]);

  const resolveAllConflicts = useCallback(async (channelId, resolution) => {
    setIsResolvingConflict(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${integrationsUrl}/${channelId}/conflicts/resolve-all`,
        { resolution },
        config
      );
      if (response.data.success) {
        const resolved = response.data.data.resolved || 0;
        showSuccessMessage?.(`Resolved ${resolved} conflict${resolved !== 1 ? 's' : ''}`);
        setConflicts(prev => prev.map(c =>
          c.status === 'pending' ? { ...c, status: `resolved_${resolution}` } : c
        ));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error resolving all conflicts:', err);
      setError?.(err.response?.data?.error || 'Failed to resolve conflicts');
      return false;
    } finally {
      setIsResolvingConflict(false);
    }
  }, [integrationsUrl, getAuthHeaders, setError, showSuccessMessage]);

  const clearResults = useCallback(() => {
    setLastSyncResult(null);
    setLastImportResult(null);
    setLastPullResult(null);
  }, []);

  return {
    integrationStatuses,
    platforms,
    isLoadingStatuses,
    isLoadingPlatforms,
    isConnecting,
    isDisconnecting,
    isSyncing,
    isImporting,
    isPulling,
    lastSyncResult,
    lastImportResult,
    lastPullResult,
    conflicts,
    isLoadingConflicts,
    isResolvingConflict,
    fetchPlatforms,
    fetchIntegrationStatuses,
    connectChannel,
    disconnectChannel,
    syncInventory,
    importSales,
    pullInventory,
    fetchConflicts,
    resolveConflict,
    resolveAllConflicts,
    clearResults,
  };
};

export default useIntegrationData;
